import type { ProportionalToRiskCoverage } from '@/features/quotes/types/group-life/coverage-schema.types';
import type { CoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';
import { getDisplaySumInsuredMax } from '@/features/quotes/utils/group-life/coverage-display.util';
import {
    clampPercentToMax,
    formatCurrencyBRL,
    formatPercentValue,
    parsePercentInput,
} from '@/features/quotes/utils/group-life/format';
import { CoverageRowShell, PercentControl } from './coverage-row-shell';

type Props = {
  coverage: ProportionalToRiskCoverage;
  selection: CoverageSelection | undefined;
  resolvedCapital: number | null;
  onChange: (next: CoverageSelection) => void;
  disabled?: boolean;
  error?: string;
  /** Coberturas básicas não exibem o detalhamento "X% de Y" nem o valor resolvido. */
  isBasic?: boolean;
};

export function ProportionalToRiskRow({
  coverage,
  selection,
  resolvedCapital,
  onChange,
  disabled,
  error,
  isBasic,
}: Props) {
  const enabled =
    selection?.kind === 'PROPORTIONAL_TO_RISK' ? selection.enabled : false;
  const override =
    selection?.kind === 'PROPORTIONAL_TO_RISK'
      ? selection.proportionOverride
      : undefined;
  const proportion = override ?? coverage.proportion;
  // InsureMO IsSIEditable === false → proporção (que define o capital) travada.
  const sumInsuredLocked = coverage.isSumInsuredEditable === false;
  // Teto de capital (`sumInsuredMax`) — ver comentário em
  // `proportional-to-coverage-row`.
  const displayMax = getDisplaySumInsuredMax(coverage);
  const maxLabel =
    displayMax != null ? `Max: ${formatCurrencyBRL(displayMax)}` : undefined;

  return (
    <CoverageRowShell
      name={coverage.name}
      description={coverage.description}
      badge={coverage.isRider ? 'Adicional' : undefined}
      caption={
        isBasic ? (
          maxLabel
        ) : (
          <>
            {formatPercentValue(proportion)}% de{' '}
            <span className='font-medium'>{coverage.riskBasisCode}</span>
            {maxLabel ? ` · ${maxLabel}` : ''}
          </>
        )
      }
      enabled={enabled}
      onToggle={next =>
        onChange({
          kind: 'PROPORTIONAL_TO_RISK',
          enabled: next,
          proportionOverride: override,
        })
      }
      disabled={disabled}
      error={error}
      control={
        <PercentControl
          percent={formatPercentValue(proportion)}
          onChangePercent={raw =>
            onChange({
              kind: 'PROPORTIONAL_TO_RISK',
              enabled,
              // Limita o percentual ao teto da cobertura (`Proportion` do schema).
              proportionOverride: clampPercentToMax(
                parsePercentInput(raw),
                coverage.proportion,
              ),
            })
          }
          resolvedLabel={
            isBasic
              ? undefined
              : enabled
                ? formatCurrencyBRL(resolvedCapital)
                : // Reserva a linha (vazia) para a altura não mudar ao ligar.
                  ''
          }
          disabled={disabled || !enabled || sumInsuredLocked}
        />
      }
    />
  );
}
